import { normalizeName } from '../../shared/naming';

/**
 * O de-para entre o GanttPRO e o catálogo do sistema — o formato, não o conteúdo.
 *
 * O cronograma do cliente fala em "grupo ESTRUTURA / serviço CONVENCIONAL"; o sistema fala
 * em frente (pavimento × disciplina) e serviço. A ponte é uma **tabela**, e não uma pilha
 * de `if`: quem mexe nela é o usuário, na conferência, e o desenvolvedor, quando o cliente
 * manda outra obra. O conteúdo do Park Europa está em `de-para-park-europa.ts`.
 *
 * Duas chaves, da mais específica para a mais geral:
 *
 * 1. **o nome da tarefa**, para as folhas que não seguem o padrão — `Fundação / Radier`,
 *    `Elevador`, `Pintura - Esmalte sobre Ferro`;
 * 2. **o par de grupos** (disciplina do cronograma + serviço do cronograma), que é o
 *    caminho confiável: o nome da tarefa varia de padrão, o grupo não.
 *
 * Uma tarefa pode virar **vários alvos** (a barra de estrutura vira PILARES, VIGAS, LAJE e
 * ESCADAS — D1) e cada alvo diz **onde** ela cai (`placement`).
 */

/**
 * Os pavimentos que as etapas da Fase 23 criam abaixo do térreo.
 *
 * O nível só ordena (RN-238): −3, −2 e −1 deixam o térreo (0) e o 1º pavimento (1) livres.
 */
export type StageFloorKey = 'PRELIMINAR' | 'FUNDACAO' | 'CONTENCAO';

export interface StageFloor {
  readonly name: string;
  readonly level: number;
}

export const STAGE_FLOORS: Readonly<Record<StageFloorKey, StageFloor>> = {
  PRELIMINAR: { name: 'Preliminar', level: -3 },
  FUNDACAO: { name: 'Fundação', level: -2 },
  CONTENCAO: { name: 'Contenção', level: -1 },
};

/** Onde o item cai: em que pavimento (ou pavimentos) o alvo gera linha. */
export type Placement =
  /** O pavimento escrito no nome da tarefa ("3º Pavimento - Tipo"). Sem ele, é problema. */
  | { readonly kind: 'TASK_FLOOR' }
  /**
   * Um item **por pavimento** do arquivo, com a mesma janela da tarefa (D2, RN-326). Para
   * a tarefa que o cronograma escreve sem local: a fachada por face, a porta corta-fogo.
   */
  | { readonly kind: 'EVERY_FLOOR' }
  /** Só o último pavimento que aparece no arquivo — a cobertura metálica (D2). */
  | { readonly kind: 'LAST_FLOOR' }
  /** Um pavimento por número — a demarcação de vaga fica no 1º (D2). */
  | { readonly kind: 'FLOOR'; readonly level: number }
  /** A etapa abaixo do térreo — a fundação e o gabarito (D1). */
  | { readonly kind: 'STAGE'; readonly stage: StageFloorKey };

export interface DeParaTarget {
  readonly disciplineName: string;
  /** `null`: a frente inteira (RN-320) — a barra que não desce até o serviço. */
  readonly serviceName: string | null;
  /**
   * A unidade do serviço, para quando ele for criado na obra (RN-325). Não é a unidade de
   * uma quantidade: o GanttPRO não traz quantidade. Na frente inteira não há serviço, e é `null`.
   */
  readonly unit: string | null;
  readonly placement: Placement;
  /** Trecho fixo do alvo ("MUROS"). Sem ele, o trecho vem do local do nome (a face da fachada). */
  readonly segment?: string;
  /**
   * O alvo nasce da **barra de estrutura** (D1). Os pavimentos que só aparecem nelas — o
   * barrilete teto — não entram na divisão das tarefas sem pavimento (D2).
   */
  readonly fromStructure?: boolean;
  /** O serviço (e a disciplina, se for o caso) ainda **não existe** e espera a aprovação do usuário (D3). */
  readonly pendingApproval?: boolean;
  /** A disciplina também é nova — AR CONDICIONADO e ELEVADOR. */
  readonly newDiscipline?: boolean;
  /** Dúvida a levar para a conversa de aprovação. */
  readonly note?: string;
  /**
   * A tarefa **não é serviço próprio**: ela se junta ao item que o mesmo alvo — mesma frente,
   * serviço e trecho — recebe de outra tarefa (D3: o corrimão da escada de incêndio entra no
   * GUARDA-CORPO). O resolvedor une as duas num item só: soma o HH e a quantidade, junta as
   * predecessoras e abre a janela para cobrir as duas. Sem a marca, dois alvos na mesma chave
   * são colisão, e o aviso diz quais (RN-320). Sozinha no arquivo, a tarefa vira o item.
   */
  readonly joinsSameItem?: boolean;
  /**
   * O peso do alvo no HH da tarefa (D5, RN-327). Quando a barra vira vários itens, o HH se
   * reparte pelos pesos, normalizados; sem peso em nenhum alvo, em partes iguais. Peso 0 é o
   * item que existe no cronograma e não gasta mão de obra própria — a armação, que é de
   * empreiteiro.
   */
  readonly laborWeight?: number;
  /**
   * O passo do alvo na ordem de execução da barra (RN-329, 02/10/2026). Quando a barra vira
   * serviços que se sucedem (a fôrma antes do concreto, o concreto antes da desmontagem), a
   * janela dela se reparte pelos dias úteis, na ordem dos passos e pelo HH de cada um. Os alvos
   * no mesmo passo dividem a mesma janela: as quatro peças da estrutura. O passo sem HH, a
   * armação de empreiteiro, fica com a janela do passo de antes. Sem passo, o alvo leva a
   * janela inteira.
   */
  readonly executionStep?: number;
}

export interface DeParaGroupEntry {
  /** O grupo da disciplina no cronograma (nível 3 da EDT): "PINTURA". */
  readonly group: string;
  /** O grupo do serviço (nível 4), **sem** o prefixo da disciplina: "1ª DEMÃO COM EMASSAMENTO". */
  readonly service: string;
  readonly targets: readonly DeParaTarget[];
}

export interface DeParaNameEntry {
  /** O nome da tarefa inteiro, como está no arquivo. */
  readonly taskName: string;
  readonly targets: readonly DeParaTarget[];
}

export interface GanttProDePara {
  readonly byGroup: readonly DeParaGroupEntry[];
  readonly byTaskName: readonly DeParaNameEntry[];
}

/** O que o usuário escolhe na conferência para um nome que o de-para não conhece. */
export interface DeParaOverride {
  readonly disciplineName: string;
  /** `null`: a frente inteira. */
  readonly serviceName: string | null;
}

/**
 * A chave pública de um grupo — a que a conferência mostra e o `overrides` recebe.
 *
 * "PINTURA / 1ª DEMÃO COM EMASSAMENTO": o grupo da disciplina e o do serviço, como estão
 * no arquivo. A comparação ignora caixa e espaço repetido, e **não** ignora acento: um
 * erro de digitação do cliente aparece como nome novo, em vez de se esconder (RN-036).
 */
export function sourceKeyOf(group: string, service: string): string {
  return `${group} / ${service}`;
}

export function deParaGroupKey(group: string, service: string): string {
  return `${normalizeName(group)}\u0000${normalizeName(service)}`;
}

export function deParaNameKey(taskName: string): string {
  return normalizeName(taskName);
}

export function overrideKey(sourceKey: string): string {
  return normalizeName(sourceKey);
}
